import React, { useEffect, useMemo, useState } from "react";
import { inject, observer } from "mobx-react";

import axios from "axios";

import { IconButton, makeStyles } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import { MdAdd } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";

import StyledButton from "./buttons/StyledButton";
import StyledSwitch from "./components/StyledSwitch";
import OpenAIOrgForm from "./components/OpenAIOrgForm";

import { cx } from "./lib/utils";
import { shimmerStyles } from "./styles";

import assistantConfig from "./configs/assistant.config";

const useStyles = makeStyles({
  ...shimmerStyles,
  tableHeadShimmer: {
    height: "40px",
    marginTop: "10px",
    width: "100%",
    borderRadius: 6,
  },
  rowShimmer: {
    height: "30px",
    marginTop: "10px",
    width: "100%",
    borderRadius: 6,
  },
  buttonShimmer: {
    height: "30px",
    marginTop: "10px",
    marginBottom: "10px",
    width: "25%",
    borderRadius: 6,
  },
});

const getOpenAIOrgs = ({ token }) =>
  axios.get(`${assistantConfig.assistantApiBaseUrl}/conf/v1/openai/orgs`, {
    headers: { Authorization: `Bearer ${token}` },
  });

const toggleOpenAIOrg = ({ orgId, token }, body) =>
  axios.post(
    `${assistantConfig.assistantApiBaseUrl}/conf/v1/openai/orgs/${orgId}/toggle`,
    body,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

const createOpenAIOrg = ({ token }, body) =>
  axios.post(
    `${assistantConfig.assistantApiBaseUrl}/conf/v1/openai/orgs`,
    body,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

const removeOpenAIOrg = ({ token }, orgId) =>
  axios.delete(
    `${assistantConfig.assistantApiBaseUrl}/conf/v1/openai/orgs/${orgId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

const OpenAISettings = ({ assistantStore }) => {
  const [openAIOrgs, setOpenAIOrgs] = useState([]);
  const [fetchingOrgs, setFetchingOrgs] = useState(true);
  const [editingOrgIndex, setEditingOrgIndex] = useState(-1);

  const classes = useStyles();

  const loadData = async () => {
    try {
      setFetchingOrgs(true);
      const { token } = await assistantStore.getChatsAccessToken();

      const res = await getOpenAIOrgs({ token });
      if (res.data) {
        setOpenAIOrgs(res.data);
      }
    } catch (e) {
    } finally {
      setFetchingOrgs(false);
    }
  };

  const handleAddApiKey = () => {
    setEditingOrgIndex(openAIOrgs.length);
  };

  const handleCancelEditOrg = () => {
    setEditingOrgIndex(-1);
  };

  const handleToggleOrgState = (org, model, i, j) => async () => {
    setOpenAIOrgs((prevOrgs) => {
      const newOrgs = [...prevOrgs];
      newOrgs[i].models[j].disabled = !newOrgs[i].models[j].disabled;

      return newOrgs;
    });
    const { token } = await assistantStore.getChatsAccessToken();

    await toggleOpenAIOrg(
      { orgId: org.id, token },
      { models: { [model.model]: !model.disabled } }
    );
  };

  const handleSubmitOpenAIOrgForm = async (values) => {
    //
    try {
      const { token } = await assistantStore.getChatsAccessToken();

      await createOpenAIOrg(
        { token },
        {
          apiKey: values.openAIApiKey,
          models: [values.openAIModel],
          tier: values.openAIOrgTier,
        }
      );

      const res = await getOpenAIOrgs({ token });
      if (res.data) {
        setOpenAIOrgs(res.data);
      }
      setEditingOrgIndex(-1);
    } catch (e) {
      console.log(`e->`, e);
    }
  };

  const handleRemoveOrg = (org, i) => async () => {
    try {
      const { token } = await assistantStore.getChatsAccessToken();

      const orgRemovedRes = await removeOpenAIOrg({ token }, org.id);
      console.log(`orgRemovedRes->`, orgRemovedRes);
      const res = await getOpenAIOrgs({ token });
      if (res.data) {
        setOpenAIOrgs(res.data);
      }
    } catch (e) {}
  };

  const shimmer = useMemo(() => {
    return (
      <div style={{ padding: "1rem 0" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div />
          <div className={cx(classes.shimmer, classes.buttonShimmer)}></div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          {Array(3)
            .fill(0)
            .map((_item, i) => {
              return (
                <div
                  key={i}
                  className={cx(classes.shimmer, classes.tableHeadShimmer)}
                />
              );
            })}
        </div>
        {Array(3)
          .fill(0)
          .map((_item, i) => {
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "1rem",
                  marginTop: "0.05rem",
                }}
              >
                {Array(3)
                  .fill(0)
                  .map((_item, j) => {
                    return (
                      <div
                        key={j}
                        className={cx(classes.shimmer, classes.rowShimmer)}
                      />
                    );
                  })}
              </div>
            );
          })}
      </div>
    );
  }, []);

  const renderOpenAIOrgs = () => {
    if (fetchingOrgs) {
      return shimmer;
    }

    return (
      <>
        {editingOrgIndex > -1 ? (
          <OpenAIOrgForm
            onSubmit={handleSubmitOpenAIOrgForm}
            onCancel={handleCancelEditOrg}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div />
            <StyledButton
              color="primary"
              startIcon={<MdAdd />}
              onClick={handleAddApiKey}
            >
              Add New Key
            </StyledButton>
          </div>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Org ID</TableCell>
                <TableCell>Models</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {openAIOrgs.map((org, i) => {
                const key = `${org.id}-${org.model}`;
                return (
                  <TableRow
                    key={key}
                    // style={{ color: org.disabled ? "#434343" : undefined }}
                  >
                    <TableCell padding={"none"}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <div>{org.id}</div>
                        <div style={{ display: "flex" }}>
                          <div
                            style={{
                              backgroundColor: "#7ddb80",
                              color: "#325633",
                              borderRadius: "12px",
                              padding: "0.2rem 0.5rem",
                            }}
                          >
                            {org.tier}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell padding="none">
                      {org.models.map((model, j) => {
                        const modelKey = `${key}-${model.model}`;
                        return (
                          <Table key={modelKey}>
                            <TableBody>
                              <TableRow>
                                <TableCell style={{ borderWidth: 0 }}>
                                  {model.model}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  padding="none"
                                  style={{ borderWidth: 0 }}
                                >
                                  {Math.round(model.tokensLimit / 1000)}k tokens
                                </TableCell>
                                <TableCell
                                  align="right"
                                  padding="none"
                                  style={{ borderWidth: 0 }}
                                >
                                  <StyledSwitch
                                    color="primary"
                                    checked={!model.disabled}
                                    onChange={handleToggleOrgState(
                                      org,
                                      model,
                                      i,
                                      j
                                    )}
                                    name={modelKey}
                                    inputProps={{
                                      "aria-label": `${modelKey}-checkbox`,
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        );
                      })}
                    </TableCell>
                    <TableCell align="center" padding="none">
                      <div style={{ position: "relative" }}>
                        <Tooltip title="Delete this OpenAI Organization">
                          <IconButton
                            fontSize="small"
                            onClick={handleRemoveOrg(org, i)}
                            style={{
                              backgroundColor: "rgba(0, 0, 0, 0.08)",
                            }}
                          >
                            <RiDeleteBin6Line size={16} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  const renderOpenAISettings = () => {
    return (
      <div style={{ padding: "1rem 0" }}>
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: "12px",
              fontWeight: "normal",
              color: "#818181",
            }}
          >
            Server OpenAI API keys
          </h3>
        </div>
        {renderOpenAIOrgs()}
      </div>
    );
  };

  useEffect(() => {
    loadData();
  }, []);

  return renderOpenAISettings();
};

export default inject(({ store }) => {
  return {
    assistantStore: store.assistantStore,
  };
})(observer(OpenAISettings));
